/**
 * A deck in the picker: legend art, name, counts, and which tiers it can be played in (owner decision
 * Q21): tier 1 and 2 for any legal deck (tier 2 lists its "manual" cards), never for an illegal one,
 * with the first validator issues and their rule citations. A Poroboard surface card (FeatureCard's
 * fill and hairline); the chosen deck has the violet edge.
 */
import { BAR_BORDER, CardImage, LOOK, StatusBadge, SURFACE, WELL } from '@play/ui';
import { AlertCircle, Check, Loader2 } from 'lucide-react';
import { useCatalog } from '../../lib/catalog';
import { cardFace, faceSources } from '../../lib/cards';
import { type MyDeck, useDeckCheck } from './decks';

/** One deck tile. */
export function DeckTile({ deck, selected, onSelect }: { deck: MyDeck; selected: boolean; onSelect: () => void }) {
  const catalog = useCatalog();
  const check = useDeckCheck(deck.id);
  const legend = deck.legend_card_id
    ? cardFace(catalog, { cardId: deck.legend_card_id, printingId: deck.legend_printing_id })
    : null;
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      className="text-left p-2.5 flex gap-3 rounded-[12px] transition-[background,box-shadow] hover:brightness-110"
      style={
        selected
          ? { background: 'rgba(117,70,255,0.12)', boxShadow: `inset 0 0 0 1px ${LOOK.violet}` }
          : { background: SURFACE, boxShadow: `inset 0 0 0 1px ${BAR_BORDER}` }
      }
      data-testid={`deck-${deck.id}`}
    >
      <span className="w-[68px] shrink-0">
        {legend ? (
          <CardImage sources={faceSources(legend, 240)} alt={legend.alt} rounded="rounded-[8px]" />
        ) : (
          <span className="block aspect-[744/1039] rounded-[8px]" style={{ background: WELL }} />
        )}
      </span>
      <span className="min-w-0 flex-1 flex flex-col gap-1 py-0.5">
        <span className="font-semibold text-[15px] leading-5 truncate" style={{ color: LOOK.text }}>
          {deck.name}
        </span>
        <span className="text-[13px] truncate" style={{ color: LOOK.sub }}>
          {legend?.name ?? 'No legend'}
        </span>
        <span className="text-[11.5px] tabular-nums" style={{ color: LOOK.muted }}>
          {deck.main_count} main · {deck.rune_count} runes · {deck.battlefield_count} battlefields
        </span>
        <span className="flex flex-wrap gap-1.5 mt-auto pt-1">
          {check.isPending ? (
            <StatusBadge
              color={LOOK.muted}
              icon={<Loader2 size={11} className="animate-spin motion-reduce:animate-none" />}
            >
              Checking
            </StatusBadge>
          ) : check.isError ? (
            <StatusBadge color={LOOK.warn}>Couldn’t check</StatusBadge>
          ) : check.data.legal ? (
            <>
              <StatusBadge color={LOOK.ok} icon={<Check size={11} />}>
                Tier 1
              </StatusBadge>
              <StatusBadge color={LOOK.ok} icon={<Check size={11} />}>
                Tier 2{check.data.manual.length ? ` · ${check.data.manual.length} manual` : ''}
              </StatusBadge>
            </>
          ) : (
            <StatusBadge color={LOOK.bad} icon={<AlertCircle size={11} />}>
              Not legal
            </StatusBadge>
          )}
        </span>
      </span>
    </button>
  );
}
